import { useMemo, useState } from 'react';
import { I } from '../components/icons.jsx';
import { useNodes, useClusterSqlRows } from '../data/use-metrics.js';

// Extract a port number from a "host:port" address. Used purely for
// display — the address itself is the canonical identifier.
function portOf(addr) {
  if (typeof addr !== 'string') return null;
  const i = addr.lastIndexOf(':');
  if (i < 0) return null;
  const n = Number(addr.slice(i + 1));
  return Number.isFinite(n) ? n : null;
}

function fmtBytes(b) {
  if (b == null || !Number.isFinite(b)) return '—';
  if (b >= 1024 ** 3) return (b / 1024 ** 3).toFixed(1) + ' GB';
  if (b >= 1024 ** 2) return (b / 1024 ** 2).toFixed(0) + ' MB';
  if (b >= 1024)      return (b / 1024).toFixed(0) + ' KB';
  return Math.round(b) + ' B';
}

function shortId(s, n = 8) {
  return typeof s === 'string' && s.length > n ? s.slice(0, n) + '…' : (s || '—');
}

// Membership card — one tile per peer in `SHOW NODES`. Shows the address,
// role for shard 0, RSS, and the share of raft groups this node is leading.
// Tile count tracks the actual cluster size (no hardcoded list).
function ClusterMembership({ perNode, leadCountByNodeId, totalGroups }) {
  if (!perNode.length) {
    return <div className="topo-empty">No nodes reported yet.</div>;
  }
  return (
    <div className="cluster-members">
      {perNode.map(n => {
        const offline = n.status !== 'alive';
        const isLeader = n.detail?.role === 'leader';
        const role = n.detail?.role;
        return (
          <div className={'member-card' + (offline ? ' offline' : '')} key={n.node}>
            <div className="member-head">
              <span className="member-port">{n.port != null ? `:${n.port}` : n.node}</span>
              {offline
                ? <span className="pill orange">{n.status || 'offline'}</span>
                : isLeader
                  ? <span className="pill cyan">leader of shard 0</span>
                  : role === 'follower'
                    ? <span className="pill green">follower</span>
                    : <span className="pill green">{role || 'alive'}</span>}
            </div>
            <dl className="member-meta">
              <dt>node id</dt>
              <dd title={n.node_id || ''}>{n.node_id ? shortId(n.node_id, 10) : <span className="muted">—</span>}</dd>
              <dt>RSS</dt>
              <dd>{fmtBytes(n.rss_bytes ?? n.memory_total_bytes)}</dd>
              <dt>leads</dt>
              <dd>
                {(() => {
                  const leads = n.node_id ? (leadCountByNodeId[n.node_id] || 0) : null;
                  if (leads == null) return <span className="muted">—</span>;
                  const sharePct = totalGroups ? Math.round((leads / totalGroups) * 100) : null;
                  return (
                    <span className={leads === 0 ? 'muted' : ''}>
                      {leads} of {totalGroups}{' '}
                      {sharePct != null && <span className="muted">({sharePct}%)</span>}
                    </span>
                  );
                })()}
              </dd>
            </dl>
          </div>
        );
      })}
    </div>
  );
}

// Leadership distribution bar chart. Counts shards-led per node_id,
// sorts desc, and warns when one node holds >80% of the leadership
// (a write-path bottleneck).
function LeadershipDistribution({ leadCountByNodeId, allNodeIds, peerById, totalGroups }) {
  if (!totalGroups) return <div className="topo-empty">No raft groups loaded yet.</div>;
  const all = new Set([...Object.keys(leadCountByNodeId), ...(allNodeIds || [])]);
  const entries = Array.from(all)
    .map(nid => [nid, leadCountByNodeId[nid] || 0])
    .sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <div className="topo-empty">No leaders elected yet.</div>;
  const top = entries[0]?.[1] ?? 0;
  const concentrated = entries.length > 1 && top / totalGroups >= 0.8;

  return (
    <div className="leadership-panel">
      {entries.map(([nodeId, count]) => {
        const port = peerById[nodeId];
        const label = port ? `:${port}` : shortId(nodeId, 10);
        const pct = totalGroups ? (count / totalGroups) * 100 : 0;
        return (
          <div className="leadership-row" key={nodeId}>
            <div className="leadership-label" title={nodeId}>{label}</div>
            <div className="leadership-track">
              <div className="leadership-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="leadership-value">{count} <span className="muted">/ {totalGroups}</span></div>
          </div>
        );
      })}
      {concentrated && (
        <div className="leadership-warn">
          Leadership is concentrated on one node — write throughput won't scale beyond that node until other peers win some elections.
        </div>
      )}
    </div>
  );
}

// Aggregate `SHOW RAFTGROUPS ON ALL NODES` (byNode: {addr: rows}) into a
// per-shard replica set: which nodes host each shard and their role. This
// is the only way to see FOLLOWER placement — `SHOW RAFTGROUPS` alone is
// node-local (the connected node's shards only). Sorted by shard id.
function aggregatePlacement(columns, byNode) {
  if (!columns || !byNode) return [];
  const idx = name => columns.indexOf(name);
  const si = idx('shard_id'), ri = idx('role'), st = idx('status'), pc = idx('peer_count');
  const map = new Map(); // shard_id -> { shard_id, status, peer_count, replicas: [{port, role}] }
  for (const [addr, rows] of Object.entries(byNode)) {
    const port = portOf(addr);
    for (const row of rows || []) {
      const shard = row[si];
      let e = map.get(shard);
      if (!e) {
        e = { shard_id: shard, status: row[st], peer_count: row[pc], replicas: [] };
        map.set(shard, e);
      }
      if (row[st]) e.status = row[st]; // prefer the reported (Active/Metadata) status
      e.replicas.push({ port: port ?? addr, role: row[ri] });
    }
  }
  return Array.from(map.values())
    .map(e => {
      const leader = e.replicas.find(r => r.role === 'leader');
      const followers = e.replicas.filter(r => r.role !== 'leader');
      return {
        ...e,
        leaderPort: leader?.port ?? null,
        followerPorts: followers.map(r => r.port),
        replicaPorts: e.replicas.map(r => r.port).sort((a, b) => Number(a) - Number(b)),
      };
    })
    .sort((a, b) => Number(a.shard_id) - Number(b.shard_id));
}

// Shard placement — one row per DATA shard, showing its leader + followers
// (the replica set). Answers "where are the followers placed?" — the thing
// the leadership chart can't show. Metadata (all nodes) + the boot default
// shard 0 are excluded; they aren't placement-spread.
function ShardPlacement({ placement }) {
  // Active data shards only. Exclude shard 0 — it's the boot-default
  // placeholder (a separate peers=1 lone group per node), which otherwise
  // aggregates into a misleading "×6" replica set.
  const dataShards = placement.filter(s => s.status === 'Active' && String(s.shard_id) !== '0');
  if (!placement.length) return <div className="topo-empty">Loading placement…</div>;
  if (!dataShards.length) return <div className="topo-empty">No data shards reported yet.</div>;
  return (
    <table className="k-table">
      <thead>
        <tr>
          <th className="num">shard</th>
          <th className="num">RF</th>
          <th>leader</th>
          <th>followers</th>
          <th>replica set</th>
        </tr>
      </thead>
      <tbody>
        {dataShards.map(s => (
          <tr key={s.shard_id}>
            <td className="num fg1">{s.shard_id}</td>
            <td className="num">×{s.replicas.length}</td>
            <td>
              {s.leaderPort != null
                ? <span className="pill cyan">:{s.leaderPort}</span>
                : <span className="muted">—</span>}
            </td>
            <td>
              {s.followerPorts.length
                ? s.followerPorts.map(p => (
                    <span key={p} className="pill green" style={{ marginRight: 4 }}>:{p}</span>
                  ))
                : <span className="muted">—</span>}
            </td>
            <td className="muted" style={{ fontFamily: 'var(--app-mono)', fontSize: 12 }}>
              {s.replicaPorts.map(p => `:${p}`).join(' · ')}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Build the global per-shard summary from `SHOW RAFTGROUPS ON ALL NODES`
// (byNode: {addr: rows}). One row per shard, using the LEADER's row as the
// authoritative view (real term/commit/applied/size); falls back to any
// reporting node if no leader is currently elected. This replaces the
// node-local `SHOW RAFTGROUPS`, which only saw the connected node's shards
// — so the Raft-groups table + leadership distribution now cover the whole
// cluster, not just the ~RF/N shards the connected node happens to host.
function aggregateGroupsGlobal(columns, byNode) {
  if (!columns || !byNode) return [];
  const idx = name => columns.indexOf(name);
  const si = idx('shard_id'), ri = idx('role');
  const bySh = new Map(); // shard_id -> chosen row (leader preferred)
  for (const rows of Object.values(byNode)) {
    for (const row of rows || []) {
      const shard = row[si];
      const cur = bySh.get(shard);
      if (!cur || (row[ri] === 'leader' && cur[ri] !== 'leader')) bySh.set(shard, row);
    }
  }
  return Array.from(bySh.values())
    .map(r => ({
      shard_id:       r[idx('shard_id')],
      role:           r[idx('role')],
      term:           r[idx('term')],
      leader_id:      r[idx('leader_id')],
      last_log_index: r[idx('last_log_index')],
      commit_index:   r[idx('commit_index')],
      applied_count:  r[idx('applied_count')],
      peer_count:     r[idx('peer_count')],
      status:         r[idx('status')],
      subtree_bytes:  r[idx('subtree_bytes')],
    }))
    .sort((a, b) => Number(a.shard_id) - Number(b.shard_id));
}

function rowsToGroupDetail(columns, rows) {
  if (!rows?.length || !columns) return null;
  const idx = name => columns.indexOf(name);
  const r = rows[0];
  return {
    shard_id:           r[idx('shard_id')],
    role:               r[idx('role')],
    term:               r[idx('term')],
    leader_id:          r[idx('leader_id')],
    last_log_index:     r[idx('last_log_index')],
    commit_index:       r[idx('commit_index')],
    applied_count:      r[idx('applied_count')],
    peers:              r[idx('peers')] || [],
    is_unapplied_split: r[idx('is_unapplied_split')],
    is_split_pending:   r[idx('is_split_pending')],
  };
}

export function ClusterPage() {
  // Cluster topology — all sourced from the connected node.
  //  • SHOW NODES                       → membership (one row per peer)
  //  • SHOW RAFTGROUPS                  → list of shards + their leader
  //  • SHOW RAFTGROUP 0 ON ALL NODES    → per-peer Raft state for shard 0
  // No /node-PORT addressing, no hardcoded peer list.
  const nodesState = useNodes(5000);
  const groupDetails = useClusterSqlRows('SHOW RAFTGROUP 0 ON ALL NODES', 3000);
  // Full cluster view: `SHOW RAFTGROUPS` is node-local (connected node's
  // shards only), so fan it out across every node. Drives BOTH the
  // per-shard summary (leader row per shard) and the replica-placement card.
  const placementRows = useClusterSqlRows('SHOW RAFTGROUPS ON ALL NODES', 4000);
  const [showRetired, setShowRetired] = useState(false);

  const groups = useMemo(
    () => aggregateGroupsGlobal(placementRows.columns, placementRows.byNode),
    [placementRows.columns, placementRows.byNode],
  );

  const shardPlacement = useMemo(
    () => aggregatePlacement(placementRows.columns, placementRows.byNode),
    [placementRows.columns, placementRows.byNode],
  );

  // Stitch per-node SHOW NODES rows together with per-node SHOW RAFTGROUP 0
  // rows (keyed by the `node` address). Either source may be missing for
  // a peer mid-poll; missing fields render as "—" rather than dropping the
  // tile.
  const perNode = useMemo(() => {
    const nodes = nodesState.nodes ?? [];
    const cols = groupDetails.columns ?? [];
    const detailByNode = {};
    if (groupDetails.byNode) {
      for (const [addr, rows] of Object.entries(groupDetails.byNode)) {
        if (rows.length === 0) continue;
        // rows[0] is one shard-0 row per node (inner cols, no `node` prefix).
        const idx = name => cols.indexOf(name);
        const r = rows[0];
        detailByNode[addr] = {
          shard_id:           r[idx('shard_id')],
          role:               r[idx('role')],
          term:               r[idx('term')],
          leader_id:          r[idx('leader_id')],
          last_log_index:     r[idx('last_log_index')],
          commit_index:       r[idx('commit_index')],
          applied_count:      r[idx('applied_count')],
          peers:              r[idx('peers')] || [],
          is_unapplied_split: r[idx('is_unapplied_split')],
          is_split_pending:   r[idx('is_split_pending')],
        };
      }
    }
    return nodes.map(n => ({
      ...n,
      port:   portOf(n.node),
      detail: detailByNode[n.node] ?? null,
    }));
  }, [nodesState.nodes, groupDetails.byNode, groupDetails.columns]);

  // Every node_id reported by SHOW NODES, so the leadership bar lists
  // every cluster member — not just the replicas of shard 0.
  const allNodeIds = useMemo(
    () => perNode.map(n => n.node_id).filter(Boolean),
    [perNode],
  );

  // node_id → address map. SHOW NODES exposes the id for every peer (not
  // just the leader), so the leadership bar can label every row.
  const peerById = useMemo(() => {
    const m = {};
    for (const n of perNode) {
      if (n.node_id) m[n.node_id] = n.port ?? n.node;
    }
    return m;
  }, [perNode]);

  // Count shards led per node_id (across all groups in SHOW RAFTGROUPS).
  // Powers the membership "leads N of M" pill and the leadership bar.
  const leadCountByNodeId = useMemo(() => {
    const m = {};
    for (const g of groups) {
      if (!g.leader_id) continue;
      m[g.leader_id] = (m[g.leader_id] || 0) + 1;
    }
    return m;
  }, [groups]);

  const detail = perNode.find(n => n.detail?.role === 'leader')?.detail;

  // KPIs
  const groupCount    = groups.length;
  const totalNodes    = perNode.length;
  const aliveCount    = perNode.filter(n => n.status === 'alive').length;
  // Replication factor = the typical peer_count of a real DATA shard.
  // NOT groups[0] — groups[0] is shard 0, the boot-default placeholder
  // with peer_count=1, which made the KPI read "×1" on a healthy RF=3
  // spread. Exclude it (and any 1-peer lone group) and take the mode of
  // peer_count across the Active data shards; metadata (status Metadata)
  // is excluded by the Active filter.
  const replication = useMemo(() => {
    const dataShards = groups.filter(
      g => g.status === 'Active' && g.peer_count != null && g.peer_count > 1,
    );
    if (!dataShards.length) return null;
    const tally = {};
    for (const g of dataShards) tally[g.peer_count] = (tally[g.peer_count] || 0) + 1;
    return Number(Object.entries(tally).sort((a, b) => b[1] - a[1])[0][0]);
  }, [groups]);
  const leaderNode    = perNode.find(n => n.detail?.role === 'leader');
  const leaderPort    = leaderNode?.port;
  const splitPending  = !!detail?.is_split_pending;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="crumbs">Admin · Cluster</div>
          <h1>Cluster topology</h1>
        </div>
        <div className="actions">
          {splitPending && <span className="pill orange"><span className="kdot orange" /> split pending</span>}
          {!splitPending && totalNodes > 0 && <span className="pill green"><span className="kdot" /> {aliveCount} of {totalNodes} nodes up</span>}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px 24px' }}>
        {/* KPI strip */}
        <div className="kpis" style={{ marginBottom: 16 }}>
          <div className="kpi-card">
            <div className="label">raft groups</div>
            <div className="value">{groupCount || '—'}</div>
            <div className="delta">{placementRows.error ? <span style={{ color: 'var(--orange)' }}>error</span> : 'one per shard'}</div>
          </div>
          <div className="kpi-card">
            <div className="label">nodes</div>
            <div className="value">{aliveCount || '—'}</div>
            <div className="delta">
              {perNode.length
                ? perNode.map(n => n.port != null ? `:${n.port}` : n.node).join(' · ')
                : 'discovering…'}
            </div>
          </div>
          <div className="kpi-card">
            <div className="label">replication factor</div>
            <div className="value">{replication != null ? `×${replication}` : '—'}</div>
            <div className="delta">peers per group</div>
          </div>
          <div className="kpi-card">
            <div className="label">leader</div>
            <div className="value" style={{ fontFamily: 'var(--app-mono)', fontSize: 22 }}>
              {leaderPort ? `:${leaderPort}` : '—'}
            </div>
            <div className="delta" title={detail?.leader_id || ''}>
              {detail ? `term ${detail.term} · ${shortId(detail.leader_id, 8)}` : '…'}
            </div>
          </div>
        </div>

        {/* Cluster membership — one tile per peer in SHOW NODES, with leadership share */}
        <div className="k-card" style={{ marginBottom: 16 }}>
          <div className="k-card-head">
            <span className="title">Cluster membership</span>
            <span className="muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
              {groupCount} raft group{groupCount === 1 ? '' : 's'} · ×{replication ?? '—'} replication
            </span>
          </div>
          <div style={{ padding: 12 }}>
            <ClusterMembership perNode={perNode} leadCountByNodeId={leadCountByNodeId} totalGroups={groupCount} />
          </div>
        </div>

        {/* Leadership distribution — answers "is write load balanced?" */}
        <div className="k-card" style={{ marginBottom: 16 }}>
          <div className="k-card-head">
            <span className="title">Leadership distribution</span>
            <span className="muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
              shards led per node
            </span>
          </div>
          <div style={{ padding: 12 }}>
            <LeadershipDistribution
              leadCountByNodeId={leadCountByNodeId}
              allNodeIds={allNodeIds}
              peerById={peerById}
              totalGroups={groupCount}
            />
          </div>
        </div>

        {/* Shard placement — where each data shard's replicas (leader + followers) live */}
        <div className="k-card" style={{ marginBottom: 16 }}>
          <div className="k-card-head">
            <span className="title">Shard placement</span>
            {placementRows.error && <span className="pill orange" style={{ marginLeft: 8 }}>error</span>}
            <span className="muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
              leader + followers per data shard
            </span>
          </div>
          <div style={{ padding: 12 }}>
            <ShardPlacement placement={shardPlacement} />
          </div>
        </div>

        {/* Raft groups table */}
        {(() => {
          // Server statuses (server.rs:5249-5285): Active, Splitting, Retired
          // for normal shards; Metadata for the cluster catalog shard. Only
          // Retired should be hidden by default — Metadata is an operational
          // shard, not a tombstone, and Splitting is a transient healthy state.
          const retiredCount = groups.filter(g => g.status === 'Retired').length;
          const visibleGroups = showRetired ? groups : groups.filter(g => g.status !== 'Retired');
          return (
            <div className="k-card" style={{ marginBottom: 16 }}>
              <div className="k-card-head">
                <span className="title">Raft groups</span>
                {placementRows.error && <span className="pill orange" style={{ marginLeft: 8 }}>error</span>}
                {retiredCount > 0 && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ marginLeft: 'auto' }}
                    onClick={() => setShowRetired(s => !s)}
                    title={showRetired ? 'Hide retired shards' : 'Show retired shards'}
                  >
                    {showRetired ? `hide retired (${retiredCount})` : `show retired (${retiredCount})`}
                  </button>
                )}
              </div>
              <table className="k-table">
                <thead>
                  <tr>
                    <th className="num">shard</th>
                    <th>status</th>
                    <th>role (leader view)</th>
                    <th className="num">term</th>
                    <th>leader</th>
                    <th className="num">log idx</th>
                    <th className="num">commit idx</th>
                    <th className="num">applied</th>
                    <th className="num">peers</th>
                    <th className="num">size</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.length === 0 ? (
                    <tr><td colSpan={10} style={{ padding: 16, color: 'var(--app-fg3)' }}>loading…</td></tr>
                  ) : visibleGroups.length === 0 ? (
                    <tr><td colSpan={10} style={{ padding: 16, color: 'var(--app-fg3)' }}>No active shards. {retiredCount} retired hidden.</td></tr>
                  ) : visibleGroups.map(g => (
                    <tr key={g.shard_id}>
                      <td className="num fg1">{g.shard_id}</td>
                      <td><span className={'pill ' + (g.status === 'Active' ? 'green' : '')}>{g.status ?? '—'}</span></td>
                      <td><span className={'pill ' + (g.role === 'leader' ? 'cyan' : 'green')}>{g.role}</span></td>
                      <td className="num">{g.term}</td>
                      <td className="fg1" style={{ fontFamily: 'var(--app-mono)', fontSize: 12 }} title={g.leader_id}>{shortId(g.leader_id)}</td>
                      <td className="num">{g.last_log_index?.toLocaleString()}</td>
                      <td className="num">{g.commit_index?.toLocaleString()}</td>
                      <td className="num">{g.applied_count?.toLocaleString()}</td>
                      <td className="num">{g.peer_count}</td>
                      <td className="num">{g.subtree_bytes != null ? fmtBytes(g.subtree_bytes) : <span className="muted">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}

        {/* Per-node raft view */}
        <div className="k-card" style={{ marginBottom: 16 }}>
          <div className="k-card-head">
            <span className="title">Per-node view</span>
          </div>
          <table className="k-table">
            <thead>
              <tr>
                <th>node</th>
                <th>role</th>
                <th className="num">term</th>
                <th>leader (this node sees)</th>
                <th className="num">log idx</th>
                <th className="num">commit</th>
                <th className="num">applied</th>
                <th className="num">RSS</th>
                <th>flags</th>
              </tr>
            </thead>
            <tbody>
              {perNode.map(n => {
                const d = n.detail;
                const offline = n.status !== 'alive';
                return (
                  <tr key={n.node}>
                    <td className="fg1" style={{ fontFamily: 'var(--app-mono)' }}>{n.port != null ? `:${n.port}` : n.node}</td>
                    <td>
                      {offline
                        ? <span className="pill orange">{n.status || 'offline'}</span>
                        : d?.role === 'leader'
                          ? <span className="pill cyan">leader</span>
                          : d?.role === 'follower'
                            ? <span className="pill green">follower</span>
                            : <span className="pill green">{d?.role || 'alive'}</span>}
                    </td>
                    <td className="num">{d?.term ?? '—'}</td>
                    <td className="fg1" style={{ fontFamily: 'var(--app-mono)', fontSize: 12 }} title={d?.leader_id || ''}>{shortId(d?.leader_id)}</td>
                    <td className="num">{d?.last_log_index?.toLocaleString() ?? '—'}</td>
                    <td className="num">{d?.commit_index?.toLocaleString() ?? '—'}</td>
                    <td className="num">{d?.applied_count?.toLocaleString() ?? '—'}</td>
                    <td className="num">{fmtBytes(n.rss_bytes ?? n.memory_total_bytes)}</td>
                    <td>
                      {d?.is_split_pending && <span className="pill orange" style={{ marginRight: 4, fontSize: 10 }}>split pending</span>}
                      {d?.is_unapplied_split && <span className="pill orange" style={{ marginRight: 4, fontSize: 10 }}>unapplied split</span>}
                      {!d?.is_split_pending && !d?.is_unapplied_split && !offline && <span style={{ color: 'var(--app-fg4)' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footnote */}
        <div style={{ color: 'var(--app-fg3)', fontSize: 12, lineHeight: 1.6, padding: '0 4px' }}>
          The membership card lists every peer reported by <span className="mono" style={{ color: 'var(--cyan)' }}>SHOW NODES</span> — node count tracks the actual cluster, not a hardcoded list. Per-node Raft state comes from <span className="mono" style={{ color: 'var(--cyan)' }}>SHOW RAFTGROUP 0 ON ALL NODES</span>, which the connected node fans out for us; the browser never has to address a non-leader directly. The per-node view still queries shard 0 only. Storage &amp; S3 are on the <a style={{ color: 'var(--cyan)' }} href="#/storage">Storage</a> page; replication-lag on <a style={{ color: 'var(--cyan)' }} href="#/monitor">Monitor</a>.
        </div>
      </div>
    </div>
  );
}
